import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Search, ExternalLink, Instagram, Phone } from 'lucide-react';
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

// The platform datatable declares this multiselect as a `string` column, so a
// saved array comes back comma-joined ("Dance Schools,Choreographers") rather
// than as an array. Split it back apart — an `Array.isArray` check fails on a
// string, and a plain `===` compare can never match a multi-value pick.
function toTypeArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (typeof v === "string") return v.split(",").map((x) => x.trim()).filter(Boolean);
  return [];
}


const HEALTH_TYPE_OPTIONS = ['Doctor', 'Dentist', 'Ayurveda', 'Yoga', 'Nutrition', 'Women', 'Health Coach'];

const HEALTH_FIELDS: FieldDef[] = [
  { key: "name", label: "Provider name", required: true },
  { key: "clinic", label: "Clinic/Facility Name", placeholder: "Nest Family Medicine" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "location", label: "Location", placeholder: "Frisco" },
  { key: "type", label: "Type", type: "multiselect", options: HEALTH_TYPE_OPTIONS },
  { key: "specialty", label: "Specialty", placeholder: "Endocrinologist" },
  { key: "mapsUrl", label: "Google Maps URL", type: "url", placeholder: "https://…" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

const LOCATION_FILTERS = ['Addison', 'Allen', 'Anna', 'Arlington', 'Aubrey', 'Carrollton', 'Cedar Hill', 'Coppell', 'Dallas', 'Denton', 'DeSoto', 'Duncanville', 'Farmers Branch', 'Flower Mound', 'Fort Worth', 'Frisco', 'Garland', 'Grand Prairie', 'Hutchins', 'Irving', 'Lewisville', 'Mansfield', 'McKinney', 'Mesquite', 'Midlothian', 'Plano', 'Richardson', 'Rockwall', 'Rowlett', 'Terrell', 'Other'];
const HERO_LOCATIONS = ['All Locations', 'Addison', 'Allen', 'Anna', 'Arlington', 'Aubrey', 'Carrollton', 'Cedar Hill', 'Coppell', 'Dallas', 'Denton', 'DeSoto', 'Duncanville', 'Farmers Branch', 'Flower Mound', 'Fort Worth', 'Frisco', 'Garland', 'Grand Prairie', 'Hutchins', 'Irving', 'Lewisville', 'Mansfield', 'McKinney', 'Mesquite', 'Midlothian', 'Plano', 'Richardson', 'Rockwall', 'Rowlett', 'Terrell', 'Other'];

const HEALTH_TYPES = [
  { name: 'Doctor', icon: '🩺' },
  { name: 'Dentist', icon: '🦷' },
  { name: 'Ayurveda', icon: '🌿' },
  { name: 'Yoga', icon: '🧘' },
  { name: 'Nutrition', icon: '🥗' },
  { name: 'Women', icon: '👩' },
  { name: 'Health Coach', icon: '🧑‍⚕️' },
];

export const HEALTH_PROVIDERS = [
  {
    id: 'dr-jasbir-dhawan',
    name: 'Dr. Jasbir Dhawan MD',
    clinic: 'Endocrinologist',
    address: '14725 Lebanon Rd #100, Frisco, TX 75035',
    location: 'Frisco',
    placeQuery: 'Dr+Jasbir+Dhawan+14725+Lebanon+Rd+Frisco+TX+75035',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=14725+Lebanon+Rd+%23100+Frisco+TX+75035',
    type: 'Doctor',
    image: '',
  },
  {
    id: 'dr-shilpa-kelkar',
    name: 'Dr. Shilpa Kelkar, Dentist',
    clinic: 'Smile Breeze Dentistry',
    address: '5285 Independence Pkwy Ste 200, Frisco, TX 75035',
    location: 'Frisco',
    placeQuery: 'Smile+Breeze+Dentistry+5285+Independence+Pkwy+Ste+200+Frisco+TX+75035',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Smile+Breeze+Dentistry+5285+Independence+Pkwy+Ste+200+Frisco+TX+75035',
    type: 'Dentist',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782832573744-rtr21i.png',
  },
  {
    id: 'dr-anurag-duggal',
    name: 'Anurag Duggal, M.D.',
    clinic: 'Infectious Disease Specialist',
    address: '13080 Dallas Parkway, Suite 320, Frisco, TX 75033',
    location: 'Frisco',
    placeQuery: 'Anurag+Duggal+13080+Dallas+Parkway+Suite+320+Frisco+TX+75033',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Anurag+Duggal+13080+Dallas+Parkway+Suite+320+Frisco+TX+75033',
    type: 'Doctor',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782782879538-yis3aq.png',
  },
  {
    id: 'dr-priti-ranjan',
    name: 'Priti Ranjan, M.D.',
    clinic: 'Nest Family Medicine',
    address: '4920 McDermott Rd Suite 200, Plano, TX 75024, TX 75033',
    location: 'Plano',
    placeQuery: 'Priti+Ranjan+4920+McDermott+Rd+Suite+200+Plano+TX+75024+TX+75033',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Priti+Ranjan+4920+McDermott+Rd+Suite+200+Plano+TX+75024+TX+75033',
    type: 'Doctor',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782783498832-i8douv.png',
  },
  {
    id: 'dr-zille-shah',
    name: 'Dr. Zille Shah MD',
    clinic: 'Family Medicine',
    address: '5899 Preston Rd Ste 1004, Frisco, TX 75034',
    location: 'Frisco',
    placeQuery: 'Dr+Zille+Shah+5899+Preston+Rd+Ste+1004+Frisco+TX+75034',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Dr+Zille+Shah+5899+Preston+Rd+Ste+1004+Frisco+TX+75034',
    type: 'Doctor',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782783957950-4fry8.png',
  },
  {
    id: 'cimply-fit-charmi-ramchandani',
    name: "Cimply Fit - Charmi Ramchandani",
    clinic: 'Guide desi perimenopausal women',
    address: '',
    location: 'Other',
    mapsUrl: '',
    type: ['Nutrition', 'Health Coach'],
    instagram: 'https://www.instagram.com/cimply_fit',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783387141445-t6hyo.jpg',
    nameColor: '#111827',
    clinicColor: '#2563EB',
  },
  {
    id: 'monaesi-skincare',
    name: 'Monaesi Skincare',
    clinic: 'Rajendra Rani Chirumamilla',
    address: '',
    location: 'Other',
    mapsUrl: '',
    type: 'Women',
    instagram: 'https://www.instagram.com/monaesiskincare/',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783430232611-n1ysod.png',
    nameColor: '#111827',
    clinicColor: '#2563EB',
  },
  {
    id: 'dr-sagar-bedi',
    name: 'Dr. Sagar Bedi',
    clinic: 'Baylor Scott & White Emergency Hospitals',
    address: '26791 US-380, Aubrey, TX 76227',
    location: 'Aubrey',
    placeQuery: 'Baylor+Scott+White+Emergency+Hospitals+26791+US-380+Aubrey+TX+76227',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=26791+US-380+Aubrey+TX+76227',
    type: 'Doctor',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783454736464-ln02mg.png',
  },
  {
    id: 'dr-shashi-alloju',
    name: 'Shashi M. Alloju, M.D.',
    clinic: 'Eye Health Texas',
    address: '6533 Preston Rd Ste 100, Plano, TX 75024',
    location: 'Plano',
    placeQuery: 'Eye+Health+Texas+6533+Preston+Rd+Ste+100+Plano+TX+75024',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=6533+Preston+Rd+Ste+100+Plano+TX+75024',
    type: 'Doctor',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783455457790-mia6n.png',
  },
  {
    id: 'dr-vishal-patel',
    name: 'Vishal Patel, M.D.',
    clinic: 'Ophthalmologist',
    address: '9955 Gillespie Dr Ste 100, Plano, TX 75025',
    location: 'Plano',
    placeQuery: 'Vishal+Patel+Ophthalmologist+9955+Gillespie+Dr+Ste+100+Plano+TX+75025',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=9955+Gillespie+Dr+Ste+100+Plano+TX+75025',
    type: 'Doctor',
    image: '',
  },
  {
    id: 'dr-lavanya-rudrapatna',
    name: 'Dr. Lavanya Rudrapatna, DMD, Dentist',
    clinic: '',
    address: '',
    location: 'Other',
    mapsUrl: '',
    // Single card that matches the "Doctor" and "Dentist" filter chips
    // (filteredProviders already handles an array type) instead of being
    // duplicated as separate default entries per filter.
    type: ['Doctor', 'Dentist'],
    website: 'https://www.waterfrontsmiles.com',
    website2: 'https://www.chaseoaksdentistry.com',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783533511459-o4dzlr.png',
  },
  {
    id: 'versallie-capote-piga',
    name: 'Versallie Capote-Piga, MD',
    clinic: 'Piga Primary Care',
    specialty: 'Pediatrician',
    address: '8380 Warren Pkwy #305, Frisco, TX 75034',
    location: 'Frisco',
    placeQuery: 'Piga+Primary+Care+8380+Warren+Pkwy+%23305+Frisco+TX+75034',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=8380+Warren+Pkwy+%23305+Frisco+TX+75034',
    type: 'Doctor',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783543328056-kaeeqe.png',
  },
  {
    id: 'ayurroots-ayurveda-wellness',
    name: 'AyurRoots Ayurveda Wellness',
    clinic: '',
    address: '6101 Chapel Hill Blvd #201, Plano, TX, United States, 75093',
    location: 'Plano',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=6101+Chapel+Hill+Blvd+%23201+Plano+TX+75093',
    type: 'Ayurveda',
    instagram: 'https://www.instagram.com/ayurroots',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784420690314-xak0l.png',
  },
  {
    id: 'lassi-protein',
    name: 'Lassi Protein',
    clinic: 'Mango Lassi Protein Powder with 25g protein per serving!',
    address: '',
    location: 'Plano',
    mapsUrl: '',
    type: 'Nutrition',
    instagram: 'https://www.instagram.com/lassiprotein/',
    image: 'https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-21/6e06c79e2948.jpeg',
    nameColor: '#111827',
    clinicColor: '#2563EB',
  },
  {
    id: 'success-door',
    name: 'The Success Door',
    clinic: 'Transformation Life Coach',
    address: 'Dallas',
    location: 'Dallas',
    mapsUrl: '',
    type: 'Health Coach',
    phone: '469-988-4134',
    website: 'https://thesuccessdoor.com',
    image: 'https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-07/ab04b18e6371.jpeg',
  },
  {
    id: 'simin-faria',
    name: 'Simin Faria - Functional Nutrition and Health Coach INHC, CFNC',
    clinic: '',
    address: '',
    location: 'Other',
    mapsUrl: '',
    type: 'Health Coach',
    instagram: 'https://www.instagram.com/the_midlife_momentum_method/',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784918060634-gt1pgc.png',
  },
];

// ── Health provider card ──
export function HealthCard({ provider, index, href }: { provider: typeof HEALTH_PROVIDERS[0]; index: number; href?: string }) {
  const googleMapsUrl = provider.mapsUrl || (provider.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(provider.address)}` : '');

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      {/* Provider image */}
      <div className="w-full h-[180px] relative overflow-hidden bg-gradient-to-br from-blue-50 to-teal-50 flex items-center justify-center">
        {provider.image ? (
          <img
            src={provider.image}
            alt={provider.name}
            className="w-full h-full object-contain"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-3xl">🩺</span>
          </div>
        )}
      </div>
      <div className="p-2 pb-1 flex-1">
        <h3
          className="text-[12px] font-bold leading-tight mb-0.5 line-clamp-2"
          style={{ color: (provider as any).nameColor || '#111827' }}
        >
          {href ? (
            <Link
              to={href}
              onClick={(e) => e.stopPropagation()}
              className="hover:underline decoration-[#2563EB]"
            >
              {provider.name}
            </Link>
          ) : (
            provider.name
          )}
        </h3>
        {provider.clinic && (
          <p
            className="text-[10px] font-semibold line-clamp-1"
            style={{ color: (provider as any).clinicColor || '#2563EB' }}
          >
            {provider.clinic}
          </p>
        )}
        {(provider as any).specialty && (
          <p className="text-[10px] text-gray-500 font-medium mb-1 line-clamp-1">{(provider as any).specialty}</p>
        )}
        {provider.address && (
          <div className="flex items-start gap-1">
            <MapPin className="w-2.5 h-2.5 text-blue-500 shrink-0 mt-0.5" />
            <span className="text-[10px] text-gray-500 font-medium leading-snug">{provider.address}</span>
          </div>
        )}
      </div>
      <div className="px-2 pb-2 pt-1 flex flex-col gap-1">
        {/* Website icon is intentionally omitted here — cards that have a
            `website`/`website2` link already render dedicated "Visit Website"
            buttons below, so showing the round website icon too would be a
            duplicate for the same link (e.g. Dr. Lavanya Rudrapatna, The
            Success Door). */}
        {!(provider as any).instagram && <SocialLinks item={{ ...provider, website: undefined }} px={20} />}
        {googleMapsUrl && (
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[10px] font-semibold py-1 rounded-md transition-colors duration-200"
          >
            <ExternalLink className="w-2.5 h-2.5" />
            View Location
          </a>
        )}
        {!googleMapsUrl && !(provider as any).instagram && (provider as any).phone && (
          <a
            href={`tel:${(provider as any).phone.replace(/[^\d+]/g, "")}`}
            className="flex items-center justify-center gap-1 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[10px] font-semibold py-1 rounded-md transition-colors duration-200"
          >
            <Phone className="w-2.5 h-2.5" />
            Call
          </a>
        )}
        {(provider as any).website && (
          <a
            href={(provider as any).website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 bg-white border border-[#3B82F6] hover:bg-blue-50 text-[#3B82F6] text-[10px] font-semibold py-1 rounded-md transition-colors duration-200"
          >
            Website
          </a>
        )}
        {(provider as any).website2 && (
          <a
            href={(provider as any).website2}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 bg-white border border-[#3B82F6] hover:bg-blue-50 text-[#3B82F6] text-[10px] font-semibold py-1 rounded-md transition-colors duration-200"
          >
            Website
          </a>
        )}
        {(provider as any).instagram && (
          <a
            href={(provider as any).instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 text-white text-[11px] font-bold py-1.5 rounded-md transition-transform duration-200 hover:scale-[1.02]"
            style={{ background: 'linear-gradient(45deg, #feda75, #fa7e1e, #d62976, #962fbf, #4f5bd5)' }}
          >
            <Instagram className="w-3.5 h-3.5" />
            Follow on Instagram
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function HealthWellness() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHeroLocation, setSelectedHeroLocation] = useState('All Locations');
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setDropdownSearch('');
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => dropdownInputRef.current?.focus(), 50);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const filteredHeroLocations = HERO_LOCATIONS.filter((loc) =>
    loc.toLowerCase().includes(dropdownSearch.toLowerCase())
  );

  const mc = useManagedCollection<any>("health_wellness", HEALTH_PROVIDERS, (s, i) => slugify(s.name || String(i)));

  const effectiveLocation = selectedLocation ?? (selectedHeroLocation !== 'All Locations' ? selectedHeroLocation : null);
  const displayLocation = effectiveLocation ?? 'Dallas-DFW';

  // Dedupe by normalized name. The admin "Add Provider" form stores each add
  // as its own independent custom row keyed by a random row id (not by name),
  // so submitting the same provider more than once — or a code default PLUS
  // a matching admin-added custom row — renders multiple cards for one
  // business. Keep the FIRST occurrence; code defaults are listed before
  // customs in mc.items, so a default always wins over an accidental
  // duplicate admin-add of the same name.
  const seenProviderNames = new Set<string>();
  const dedupedProviders = mc.items.filter((provider) => {
    const key = String(provider.name || '').trim().toLowerCase();
    if (!key) return true;
    if (seenProviderNames.has(key)) return false;
    seenProviderNames.add(key);
    return true;
  });

  const filteredProviders = dedupedProviders.filter((provider) => {
    const matchesQuery = searchQuery.trim() === '' || provider.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = !effectiveLocation || provider.location === effectiveLocation;
    const matchesType = !selectedType || toTypeArray((provider as any).type).includes(selectedType);
    return matchesQuery && matchesLocation && matchesType;
  });

  // Respect the admin-managed order (drag-reorder) instead of alphabetical, so
  // editing a card doesn't move it and reordering persists on the public site.
  const orderedProviders = [...filteredProviders].sort((a: any, b: any) => (a._order ?? 0) - (b._order ?? 0));

  // De-dupe by name (case/space-insensitive). Earlier iterations of this file
  // added "Dr. Lavanya Rudrapatna, DMD" as separate Dentist-only and
  // Clinic-only custom entries before being consolidated into ONE default
  // entry with type: ['Dentist', 'Clinic'] — those earlier custom rows can
  // still be sitting in the admin-managed store (mc_health_wellness) and
  // would otherwise render as extra duplicate cards. Keep only the first
  // occurrence per name.
  const seenNames = new Set<string>();
  const sortedProviders = orderedProviders.filter((p: any) => {
    const key = String(p.name || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (!key) return true;
    if (seenNames.has(key)) return false;
    seenNames.add(key);
    return true;
  });

  const hasActiveFilters = !!selectedType;

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        .filter-scroll::-webkit-scrollbar { display: none; }
        .filter-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Hero */}
      <section className="relative w-full" style={{ minHeight: "160px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-07/bcef0e4e86b9.png"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: 'absolute', left: 0, bottom: '-1px', width: '100%', height: '18px', display: 'block' }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="relative max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex items-center justify-center" style={{ zIndex: 10, minHeight: "160px" }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="w-full max-w-[740px]">
            <div className="rounded-md" style={{ boxShadow: '0 0 40px 12px rgba(255,255,255,0.45), 0 0 80px 24px rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <div className="flex flex-col sm:flex-row bg-white rounded-md shadow-md h-auto sm:h-[40px]">
                <div className="flex-1 px-3 py-2 sm:py-0 flex items-center border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px]">
                  <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">Find a provider</span>
                  <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="" className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent" />
                </div>
                <div ref={dropdownRef} className="relative border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px] sm:w-[170px] md:w-[190px] shrink-0" style={{ zIndex: 99999 }}>
                  <button onClick={() => setShowDropdown(!showDropdown)} className="w-full h-full px-3 py-2 sm:py-0 flex items-center gap-1.5 text-left min-h-[40px]">
                    <MapPin className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{selectedHeroLocation}</span>
                    <svg className={'w-3 h-3 text-gray-400 shrink-0 transition-transform ' + (showDropdown ? 'rotate-180' : '')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showDropdown && (
                    <div
                      className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg overflow-hidden"
                      style={{ zIndex: 99999, marginTop: '4px', width: '200px', boxShadow: '0 10px 24px rgba(15,23,42,0.16)', fontFamily: "'Poppins', sans-serif" }}
                    >
                      <div className="px-3 py-2 border-b border-gray-100">
                        <input
                          ref={dropdownInputRef}
                          type="text"
                          value={dropdownSearch}
                          onChange={(e) => setDropdownSearch(e.target.value)}
                          placeholder="Search locations..."
                          className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent"
                        />
                      </div>
                      <div className="overflow-y-auto" style={{ maxHeight: '200px' }}>
                        {filteredHeroLocations.length === 0 ? (
                          <div className="px-3 py-4 text-center text-[12px] text-gray-400">No locations found</div>
                        ) : (
                          filteredHeroLocations.map((loc) => (
                            <button
                              key={loc}
                              onClick={() => { setSelectedHeroLocation(loc); setShowDropdown(false); setDropdownSearch(''); }}
                              className="w-full text-left flex items-center transition-colors duration-150"
                              style={{
                                padding: '8px 12px',
                                fontSize: '13px',
                                fontWeight: selectedHeroLocation === loc ? 700 : 500,
                                color: selectedHeroLocation === loc ? '#2563EB' : '#111827',
                                background: selectedHeroLocation === loc ? '#eff6ff' : 'transparent',
                                height: '36px',
                              }}
                              onMouseEnter={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = '#eff6ff'; }}
                              onMouseLeave={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                            >
                              {loc}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <button onClick={() => setShowDropdown(false)} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[13px] font-bold px-5 py-2.5 sm:py-0 transition-colors duration-200 shrink-0 min-h-[40px] sm:rounded-r-md w-full sm:w-auto">
                  <span className="flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5 sm:hidden" />
                    Search
                  </span>
                </button>
              </div>
            </div>

          </motion.div>
        </div>
      </section>
      <NewsTicker />

      {/* ── Health & Wellness Listings ── */}
      <section className="bg-gray-50/50 py-8">
        <div className="max-w-[1320px] mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <h2 className="text-[18px] sm:text-[20px] md:text-[22px] font-bold text-gray-900 mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Dallas Health & Wellness Scoop
            </h2>
            {/* Explore by Type */}
            <p className="text-[11px] sm:text-[12px] font-semibold text-gray-500 uppercase tracking-wider mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>Explore by Type</p>
            {/* Type filter chips */}
            <div className="flex items-center gap-2 mb-4 overflow-x-auto filter-scroll pb-1">
              {HEALTH_TYPES.map((t) => (
                <button
                  key={t.name}
                  type="button"
                  onClick={() => setSelectedType(selectedType === t.name ? null : t.name)}
                  className={"shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] sm:text-[12px] font-medium border transition-all duration-200 " +
                    (selectedType === t.name ? "bg-blue-50 text-blue-700 border-blue-300" : "bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:bg-blue-50/50")}
                >
                  <span>{t.icon}</span>
                  {t.name}
                </button>
              ))}
              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={() => { setSelectedType(null); }}
                  className="flex items-center gap-1.5 rounded-full border border-dashed whitespace-nowrap transition-colors duration-150 shrink-0"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    height: '35px',
                    padding: '0 13px',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    color: '#6b7280',
                    background: '#ffffff',
                    borderColor: '#d1d5db',
                    boxShadow: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#3B82F6';
                    e.currentTarget.style.borderColor = '#3B82F6';
                    e.currentTarget.style.background = '#eff6ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#6b7280';
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.background = '#ffffff';
                  }}
                >
                  ✕ Clear Filters
                </button>
              )}
            </div>
            <p className="text-[12px] sm:text-[13px] text-gray-500 mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Find Indian and South Asian health professionals, wellness centres, and holistic health services across DFW.
            </p>
            <AdminAddBar mc={mc} fields={HEALTH_FIELDS} label="Add Provider" />

            {/* Providers Grid */}
            {sortedProviders.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {sortedProviders.map((provider, index) => (
                  <AdminItem key={provider._key} mc={mc} item={provider} fields={HEALTH_FIELDS} title="Provider">
                    <HealthCard provider={provider} index={index} href={`/health-wellness/${provider._key}`} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-400 text-[14px]">No health providers found for this search.</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
